/**
 * Utilidades de permisos granulares.
 * Los permisos se guardan en localStorage como JSON: {"leads.create": true, ...}
 */

let cachedPermissions = null;
let cachedRaw = null;

/**
 * Retorna el objeto completo de permisos del usuario actual.
 * Usa cache para evitar JSON.parse repetidos en cada render.
 */
export const getPermissions = () => {
  try {
    const raw = localStorage.getItem('permissions') || '{}';
    if (raw !== cachedRaw) {
      cachedRaw = raw;
      cachedPermissions = JSON.parse(raw);
    }
    return cachedPermissions;
  } catch (e) {
    console.error('[permissions] Failed to parse permissions from localStorage. Clearing corrupted data.', e);
    localStorage.removeItem('permissions');
    cachedRaw = null;
    cachedPermissions = {};
    return cachedPermissions;
  }
};

/**
 * Si no hay permisos granulares configurados, admin tiene acceso total.
 */
const isLegacyAdmin = () => {
  const perms = getPermissions();
  const role = localStorage.getItem('role');
  return Object.keys(perms).length === 0 && role === 'admin';
};

/**
 * Verifica si el usuario tiene un permiso específico.
 * @param {string} key - Ej: "leads.create"
 * @returns {boolean}
 */
export const hasPermission = (key) => {
  if (isLegacyAdmin()) return true;
  const perms = getPermissions();
  return perms[key] === true;
};

/**
 * Verifica si el usuario tiene AL MENOS UNO de los permisos indicados.
 * @param {string[]} keys - Ej: ["leads.list", "clients.list"]
 * @returns {boolean}
 */
export const hasAnyPermission = (keys) => {
  if (isLegacyAdmin()) return true;
  const perms = getPermissions();
  return keys.some((key) => perms[key] === true);
};

/**
 * Verifica si el usuario tiene TODOS los permisos indicados.
 * @param {string[]} keys
 * @returns {boolean}
 */
export const hasAllPermissions = (keys) => {
  if (isLegacyAdmin()) return true;
  const perms = getPermissions();
  return keys.every((key) => perms[key] === true);
};

/**
 * Verifica si el usuario tiene algún permiso en un módulo.
 * @param {string} module - Ej: "leads"
 * @returns {boolean}
 */
export const hasModuleAccess = (module) => {
  if (isLegacyAdmin()) return true;
  const perms = getPermissions();
  return Object.keys(perms).some(
    (key) => key.startsWith(module + '.') && perms[key] === true
  );
};

/**
 * Invalida la cache de permisos. Llamar al hacer login/logout.
 */
export const clearPermissionsCache = () => {
  cachedRaw = null;
  cachedPermissions = null;
};
